import store from '@/store'

const FINAL_STATES = ['approved', 'rejected', 'not_approved', 'scheduled', 'canceled']

const STATUS_VARIANT_MAP = {
  in_progress: 'secondary',
  in_review: 'primary',
  approved: 'success',
  rejected: 'danger',
  canceled: 'dark',
  reassigned: 'warning',
}

const STATUS_ACTIONS_MAP = {
  in_progress: ['submit', 'cancel'],
  in_review: ['approve', 'reject', 'reassign'],
  approved: ['close', 'renew'],
  rejected: [],
  canceled: [],
  reassigned: ['submit'],
}

export function getStatusName(item) {
  const status = item && item.status
  if (!status) return ''
  return String(typeof status === 'object' ? (status.name || status) : status).toLowerCase().replace(/\s/g, '_')
}

export function getAssignedIdFromItem(item) {
  const assigned = (item && item.assigned_to) || (item && item.assignedTo)
  if (assigned && typeof assigned === 'object' && assigned.id != null) return assigned.id
  if (item && item.assigned_to_id != null) return item.assigned_to_id
  if (item && item.assignedTo_id != null) return item.assignedTo_id
  return null
}

export function getAssignedEmailFromItem(item) {
  const assigned = (item && item.assigned_to) || (item && item.assignedTo)
  if (assigned && typeof assigned === 'object' && assigned.email) return assigned.email
  return null
}

function getAssigneeIdFromItemOrHistory(item) {
  const fromItem = getAssignedIdFromItem(item)
  if (fromItem != null && fromItem !== '') return fromItem
  const statusName = getStatusName(item)
  if (!['in_progress', 'reassigned', 'approved'].includes(statusName)) return null
  const history = getStatusHistory(item)
  const inProgressEntry = getLastStatusEntryByState(history, 'in_progress')
  const reassignedEntry = getLastStatusEntryByState(history, 'reassigned')
  const getDate = (e) => e && (e.changed_at || e.created_at)
  const entries = [inProgressEntry, reassignedEntry].filter(Boolean)
  const sorted = entries.sort((a, b) => new Date(getDate(b) || 0) - new Date(getDate(a) || 0))
  const lastEntry = sorted[0]
  return lastEntry ? getAssignedIdFromStatusEntry(lastEntry) : null
}

function getAssigneeEmailFromItemOrHistory(item) {
  const fromItem = getAssignedEmailFromItem(item)
  if (fromItem != null && fromItem !== '') return fromItem
  const statusName = getStatusName(item)
  if (!['in_progress', 'reassigned', 'approved'].includes(statusName)) return null
  const history = getStatusHistory(item)
  const inProgressEntry = getLastStatusEntryByState(history, 'in_progress')
  const reassignedEntry = getLastStatusEntryByState(history, 'reassigned')
  const getDate = (e) => e && (e.changed_at || e.created_at)
  const entries = [inProgressEntry, reassignedEntry].filter(Boolean)
  const sorted = entries.sort((a, b) => new Date(getDate(b) || 0) - new Date(getDate(a) || 0))
  const lastEntry = sorted[0]
  return lastEntry ? getAssignedEmailFromStatusEntry(lastEntry) : null
}

export function isCurrentUserTheAssignee(item) {
  if (!item) return false
  const assignedId = getAssigneeIdFromItemOrHistory(item)
  const assignedEmail = getAssigneeEmailFromItemOrHistory(item)
  const currentUserId = getLocalUserId()
  const currentUserEmail = getLocalUserEmail()
  return matchUser(currentUserId, currentUserEmail, assignedId, assignedEmail)
}

function getCreatorIdFromItem(item) {
  if (!item) return null
  const creator = item.user_create || item.created_by || item.userCreate || item.createdBy
  if (creator && typeof creator === 'object' && creator.id != null) return creator.id
  if (typeof item.created_by === 'number' && item.created_by > 0) return item.created_by
  if (item.user_create_id != null && item.user_create_id > 0) return item.user_create_id
  if (item.created_by_id != null && item.created_by_id > 0) return item.created_by_id
  return null
}

function getStatusHistory(item) {
  const history = (item && item.status_history) || (item && item.statuses) || []
  return Array.isArray(history) ? history : []
}

function coalesce(...values) {
  for (const v of values) {
    if (v != null && v !== undefined) return v
  }
  return null
}

export function getLastStatusEntryByState(history, state) {
  const normalized = String(state || '').toLowerCase()
  const getDate = (e) => e && (e.changed_at || e.created_at)
  const sorted = [...history].sort((a, b) => new Date(getDate(b) || 0) - new Date(getDate(a) || 0))
  const getEntryStatusName = (s) => String(getStatusNameFromEntry(s) || '').toLowerCase()
  return sorted.find((s) => getEntryStatusName(s) === normalized)
}

function getAssignedIdFromStatusEntry(entry) {
  if (!entry) return null
  const changedBy = entry.changedBy
  return coalesce(
    entry.changed_by,
    changedBy && typeof changedBy === 'object' ? changedBy.id : null,
    entry.id_user,
    entry.user_id,
    entry.reviewer_id,
    entry.id_reviewer,
    entry.user && typeof entry.user === 'object' ? entry.user.id : null,
    entry.userId
  )
}

function getAssignedEmailFromStatusEntry(entry) {
  if (!entry) return null
  const changedBy = entry.changedBy
  return coalesce(
    changedBy && typeof changedBy === 'object' ? changedBy.email : null,
    entry.user && typeof entry.user === 'object' ? entry.user.email : null,
    entry.email
  )
}

function matchUser(currentUserId, currentUserEmail, assignedId, assignedEmail) {
  const idMatch =
    Number(assignedId) > 0 &&
    Number(currentUserId) > 0 &&
    Number(assignedId) === Number(currentUserId)
  const emailMatch =
    assignedEmail &&
    currentUserEmail &&
    String(assignedEmail).toLowerCase() === String(currentUserEmail).toLowerCase()
  return !!(idMatch || emailMatch)
}

export function getLocalUserId() {
  try {
    const raw = localStorage.getItem('user') || localStorage.getItem('userData') || ''
    if (!raw) return null
    const obj = JSON.parse(raw)
    const candidates = [
      obj && obj.id,
      obj && obj.user && obj.user.id,
      obj && obj.data && obj.data.id,
      obj && obj.user_id,
      obj && obj.uid,
    ]
    for (const c of candidates) {
      const n = Number(c)
      if (Number.isFinite(n) && n > 0) return n
    }
    return null
  } catch (_) {
    return null
  }
}

export function getLocalUserEmail() {
  try {
    const raw = localStorage.getItem('user') || localStorage.getItem('userData') || ''
    if (!raw) return null
    const obj = JSON.parse(raw)
    const candidates = [
      obj && obj.email,
      obj && obj.user && obj.user.email,
      obj && obj.data && obj.data.email,
    ]
    for (const c of candidates) {
      if (c && typeof c === 'string' && c.includes('@')) return c
    }
    return null
  } catch (_) {
    return null
  }
}

export function isCreatorOrAssigned(item, getUserId = getLocalUserId, getEmail = getLocalUserEmail) {
  const statusName = getStatusName(item)
  if (!['in_progress', 'reassigned'].includes(statusName)) return false

  const history = getStatusHistory(item)
  const lastEntry = getLastStatusEntryByState(history, statusName)
  const assignedIdFromItem = getAssignedIdFromItem(item)
  const assignedEmailFromItem = getAssignedEmailFromItem(item)
  let assignedId = coalesce(assignedIdFromItem, getAssignedIdFromStatusEntry(lastEntry))
  let assignedEmail = coalesce(assignedEmailFromItem, getAssignedEmailFromStatusEntry(lastEntry))

  if (!assignedId && !assignedEmail) {
    assignedId = coalesce(getCreatorIdFromItem(item), getAssignedIdFromStatusEntry(lastEntry))
  }

  const currentUserId = typeof getUserId === 'function' ? getUserId() : getUserId
  const currentUserEmail = typeof getEmail === 'function' ? getEmail() : getEmail
  return matchUser(currentUserId, currentUserEmail, assignedId, assignedEmail)
}

export function isReviewer(item, getUserId = getLocalUserId, getEmail = getLocalUserEmail) {
  const statusName = getStatusName(item)
  if (statusName !== 'in_review') return false

  const history = getStatusHistory(item)
  const lastReview = getLastStatusEntryByState(history, 'in_review')
  const assignedId = getAssignedIdFromStatusEntry(lastReview)
  const assignedEmail = getAssignedEmailFromStatusEntry(lastReview)

  const currentUserId = typeof getUserId === 'function' ? getUserId() : getUserId
  const currentUserEmail = typeof getEmail === 'function' ? getEmail() : getEmail
  return matchUser(currentUserId, currentUserEmail, assignedId, assignedEmail)
}

export function canSendToReview(item) {
  const statusName = getStatusName(item)
  if (FINAL_STATES.includes(statusName)) return false
  if (statusName !== 'in_progress' && statusName !== 'reassigned') return false
  return isCreatorOrAssigned(item)
}

export function canReview(item, $can = null) {
  try {
    if ($can && typeof $can === 'function' && !$can('review_policy_exception', 'PolicyException')) {
      return false
    }
  } catch (_) {
  }
  return isReviewer(item)
}

export function isCurrentAssigneeOrReviewer(item, getUserId = getLocalUserId, getEmail = getLocalUserEmail) {
  const statusName = getStatusName(item)
  if (statusName === 'in_review') return isReviewer(item, getUserId, getEmail)
  if (statusName === 'in_progress' || statusName === 'reassigned') return isCreatorOrAssigned(item, getUserId, getEmail)
  return false
}

export function canShowReassignAsNonAssignee(item, $can = null) {
  if (!item) return false
  const statusName = getStatusName(item)
  if (!['in_progress', 'reassigned', 'in_review'].includes(statusName)) return false
  try {
    if (!$can || typeof $can !== 'function' || !$can('special_permission_assign_new_user', 'PolicyException')) return false
  } catch (_) {
    return false
  }
  return !isCurrentAssigneeOrReviewer(item)
}

export function isCurrentUserTheRejector(item, getUserId = getLocalUserId, getEmail = getLocalUserEmail) {
  if (!item) return false
  if (getStatusName(item) !== 'rejected') return false
  const history = getStatusHistory(item)
  const rejectedEntry = getLastStatusEntryByState(history, 'rejected')
  if (!rejectedEntry) return false
  const entryUserId = getAssignedIdFromStatusEntry(rejectedEntry)
  const entryUserEmail = getAssignedEmailFromStatusEntry(rejectedEntry)
  const currentUserId = typeof getUserId === 'function' ? getUserId() : getUserId
  const currentUserEmail = typeof getEmail === 'function' ? getEmail() : getEmail
  return matchUser(currentUserId, currentUserEmail, entryUserId, entryUserEmail)
}

export function canReassignAfterRejected(item, $can = null) {
  const statusName = getStatusName(item)
  if (statusName !== 'rejected') return false
  try {
    if ($can && typeof $can === 'function' && !$can('review_policy_exception', 'PolicyException')) {
      return false
    }
  } catch (_) {
  }
  return true
}

export function canShowSpecialButtonsBlock(item, $can = null) {
  if (!item) return false
  const statusName = getStatusName(item)
  if (statusName === 'canceled' || statusName === 'approved') return false
  try {
    if (!$can || typeof $can !== 'function' || !$can('special_permission_assign_new_user', 'PolicyException'))
      return false
  } catch (_) {
    return false
  }
  return true
}

export function showReviewActions(item) {
  return getStatusName(item) === 'in_review'
}

export function canEditForm(item) {
  const statusName = getStatusName(item)
  if (FINAL_STATES.includes(statusName)) return false
  if (statusName === 'in_review') return isReviewer(item)
  if (statusName === 'in_progress' || statusName === 'reassigned') return isCreatorOrAssigned(item)
  return false
}

export function buildUserOptionLabel(user) {
  const parts = [user.name, user.user, user.email].filter(Boolean)
  return parts.join(' | ')
}

export async function fetchReviewerOptions() {
  return store.dispatch('policyException/fetchReviewers')
}

export async function fetchReassignableOptions() {
  return store.dispatch('policyException/fetchReassignable')
}

export function getItemId(item) {
  if (!item || typeof item !== 'object') return null
  const candidates = [
    item.id,
    item.id_policy_exception,
    item.ID,
    item.attributes && item.attributes.id,
    item.data && item.data.id,
  ]
  for (const candidate of candidates) {
    if (candidate == null || candidate === '') continue
    const num = Number(candidate)
    if (Number.isFinite(num) && num > 0) return num
    if (typeof candidate === 'string' && candidate.trim()) return candidate
  }
  return null
}

export function getStatusVariant(statusName) {
  if (!statusName) return 'secondary'
  const normalized = String(statusName).toLowerCase().replace(/\s/g, '_')
  return STATUS_VARIANT_MAP[normalized] || 'secondary'
}

export function getAvailableActions(statusName) {
  if (!statusName) return []
  const normalized = String(statusName).toLowerCase().replace(/\s/g, '_')
  const actions = STATUS_ACTIONS_MAP[normalized] || []
  return actions.filter((action) => action !== 'close' && action !== 'renew')
}

export function isEditDisabled(statusName) {
  if (!statusName) return false
  const normalized = String(statusName).toLowerCase().replace(/\s/g, '_')
  return normalized === 'canceled'
}

export function getStatusComment(entry) {
  if (!entry) return ''
  return entry.comment || entry.comments || ''
}

export function getStatusHistoryEntries(item) {
  return getStatusHistory(item)
}

export function getStatusNameFromEntry(entry) {
  if (!entry) return ''
  const candidates = [
    entry.status_name,
    entry.new_status && typeof entry.new_status === 'object' ? entry.new_status.name : null,
    entry.status && typeof entry.status === 'object' ? entry.status.name : entry.status,
    entry.name,
  ]
  for (const c of candidates) {
    if (c != null && c !== '') return String(c)
  }
  return ''
}

export function getUserNameFromEntry(entry) {
  if (!entry) return ''
  const changedBy = entry.changedBy
  if (entry.user_name) return entry.user_name
  if (changedBy && typeof changedBy === 'object' && changedBy.name) return changedBy.name
  if (entry.user && typeof entry.user === 'object' && entry.user.name) return entry.user.name
  if (typeof entry.user === 'string') return entry.user
  return ''
}

export function getUserEmailFromEntry(entry) {
  if (!entry) return ''
  const changedBy = entry.changedBy
  if (changedBy && typeof changedBy === 'object' && changedBy.email) return changedBy.email
  if (entry.user && typeof entry.user === 'object' && entry.user.email) return entry.user.email
  if (typeof entry.user === 'string' && entry.user.includes('@')) return entry.user
  return ''
}

export function getHistoryDate(entry) {
  if (!entry) return null
  return entry.changed_at || entry.created_at
}
